"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import { ApiWaveVotersPage } from "@/generated/models/ApiWaveVotersPage";
import { ApiWaveVoter } from "@/generated/models/ApiWaveVoter";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
interface UseWaveTopVotersProps {
  readonly waveId: string;
  readonly connectedProfileHandle: string | undefined;
  readonly reverse: boolean;
  readonly dropId: string | null;
  readonly sortDirection?: "ASC" | "DESC";
  readonly sort?: "ABSOLUTE" | "POSITIVE" | "NEGATIVE";
  readonly refetchInterval?: number;
}
export function useWaveTopVoters(t0) {
  const $ = _c(40);
  const {
    waveId,
    connectedProfileHandle,
    reverse,
    dropId,
    sortDirection: t1,
    sort: t2,
    refetchInterval: t3
  } = t0;
  const sortDirection = t1 === undefined ? "ASC" : t1;
  const sort = t2 === undefined ? "ABSOLUTE" : t2;
  const refetchInterval = t3 === undefined ? Infinity : t3;
  const queryClient = useQueryClient();
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const [voters, setVoters] = useState(t4);
  let t5;
  if ($[1] !== dropId || $[2] !== sort || $[3] !== sortDirection || $[4] !== waveId) {
    t5 = [QueryKey.WAVE_VOTERS, {
      waveId,
      dropId,
      sortDirection,
      sort
    }];
    $[1] = dropId;
    $[2] = sort;
    $[3] = sortDirection;
    $[4] = waveId;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const queryKey = t5;
  let t6;
  if ($[6] !== dropId || $[7] !== queryClient || $[8] !== queryKey || $[9] !== sort || $[10] !== sortDirection || $[11] !== waveId) {
    t6 = () => {
      queryClient.prefetchInfiniteQuery({
        queryKey,
        queryFn: async t7 => {
          const {
            pageParam
          } = t7;
          const params = {
            page_size: "20",
            sort_direction: sortDirection,
            sort
          };
          if (dropId) {
            params.drop_id = dropId;
          }
          if (pageParam !== null) {
            params.page = pageParam.toString();
          }
          return await commonApiFetch({
            endpoint: `waves/${waveId}/voters`,
            params
          });
        },
        initialPageParam: null,
        getNextPageParam: _temp,
        pages: 3,
        staleTime: 60000,
        ...getDefaultQueryRetry()
      });
    };
    $[6] = dropId;
    $[7] = queryClient;
    $[8] = queryKey;
    $[9] = sort;
    $[10] = sortDirection;
    $[11] = waveId;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== dropId || $[14] !== sort || $[15] !== sortDirection || $[16] !== waveId) {
    t7 = [waveId, dropId, sortDirection, sort];
    $[13] = dropId;
    $[14] = sort;
    $[15] = sortDirection;
    $[16] = waveId;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useEffect(t6, t7);
  let t8;
  if ($[18] !== dropId || $[19] !== sort || $[20] !== sortDirection || $[21] !== waveId) {
    t8 = async t9 => {
      const {
        pageParam: pageParam_0
      } = t9;
      const params_0 = {
        page_size: "20",
        sort_direction: sortDirection,
        sort
      };
      if (dropId) {
        params_0.drop_id = dropId;
      }
      if (pageParam_0 !== null) {
        params_0.page = pageParam_0.toString();
      }
      return await commonApiFetch({
        endpoint: `waves/${waveId}/voters`,
        params: params_0
      });
    };
    $[18] = dropId;
    $[19] = sort;
    $[20] = sortDirection;
    $[21] = waveId;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const t9 = !!connectedProfileHandle;
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = getDefaultQueryRetry();
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== queryKey || $[25] !== refetchInterval || $[26] !== t8 || $[27] !== t9) {
    t11 = {
      queryKey,
      queryFn: t8,
      initialPageParam: null,
      getNextPageParam: _temp2,
      placeholderData: keepPreviousData,
      enabled: t9,
      staleTime: 60000,
      refetchInterval,
      ...t10
    };
    $[24] = queryKey;
    $[25] = refetchInterval;
    $[26] = t8;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(t11);
  let t12;
  let t13;
  if ($[29] !== data || $[30] !== reverse) {
    t12 = () => {
      setVoters(prev => {
        const newVoters = (data?.pages ? data.pages : []).flatMap(_temp3);
        return reverse ? [...newVoters].reverse() : newVoters;
      });
    };
    t13 = [data, reverse];
    $[29] = data;
    $[30] = reverse;
    $[31] = t12;
    $[32] = t13;
  } else {
    t12 = $[31];
    t13 = $[32];
  }
  useEffect(t12, t13);
  let t14;
  if ($[33] !== fetchNextPage || $[34] !== hasNextPage || $[35] !== isFetchingNextPage || $[36] !== isLoading || $[37] !== refetch || $[38] !== voters) {
    t14 = {
      voters,
      fetchNextPage,
      hasNextPage,
      isLoading,
      isFetchingNextPage,
      refetch
    };
    $[33] = fetchNextPage;
    $[34] = hasNextPage;
    $[35] = isFetchingNextPage;
    $[36] = isLoading;
    $[37] = refetch;
    $[38] = voters;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  return t14;
}
function _temp3(page) {
  return page.data;
}
function _temp2(lastPage_0, allPages_0) {
  if (!lastPage_0.next) {
    return null;
  }
  const currentPage_0 = typeof lastPage_0.page === "number" ? lastPage_0.page : allPages_0.length - 1;
  return currentPage_0 + 1;
}
function _temp(lastPage, allPages) {
  if (!lastPage.next) {
    return null;
  }
  const currentPage = typeof lastPage.page === "number" ? lastPage.page : allPages.length - 1;
  return currentPage + 1;
}